//src/pages/ChatWithAI/hooks/useChatWithTools.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { chatFetchWithAuth } from '../utils/chatAuthHelper';

export function useChatWithTools({
  model = null, // sem id fixo: o backend resolve o modelo quando não vem no pedido
  systemPrompt = '',
  contextContent = '',
  useTools = false,
  casoId = null,
  codCli = null,
  codProcesso = null,
  threadId = null,
  reasoningMode = 'fast',
  useWebSearch = false,
  useVerbosity = false,
  refreshAccessToken = null
} = {}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [internalThreadId, setInternalThreadId] = useState(threadId);

  const threadIdRef = useRef(threadId);

  useEffect(() => {
    threadIdRef.current = threadId;
    setInternalThreadId(threadId);
  }, [threadId]);

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value || '');
  }, []);

  const getReasoningParams = useCallback((modelId, mode) => {
    if (!modelId || mode === 'fast') {
      return {
        reasoningEffort: 'low',
        thinkingLevel: 'low'
      };
    }
    return {
      reasoningEffort: 'high',
      thinkingLevel: 'high'
    };
  }, []);

  // ✅ extrai delta em vários formatos possíveis
  const extractTextDelta = (payload) => {
    if (!payload || typeof payload !== 'object') return '';

    if (typeof payload.content === 'string') return payload.content;
    if (typeof payload.textDelta === 'string') return payload.textDelta;
    if (typeof payload.delta === 'string') return payload.delta;
    if (typeof payload.text === 'string') return payload.text;

    if (payload.type === 'text-delta' && typeof payload.textDelta === 'string') return payload.textDelta;

    return '';
  };

  const appendAssistantText = (delta) => {
    if (!delta) return;

    setMessages(prev => {
      const next = [...prev];
      const lastIndex = next.length - 1;
      if (lastIndex >= 0 && next[lastIndex].role === 'assistant') {
        next[lastIndex] = {
          ...next[lastIndex],
          content: (next[lastIndex].content || '') + delta
        };
      }
      return next;
    });
  };

  const setAssistantSources = (sources) => {
    if (!sources) return;

    setMessages(prev => {
      const next = [...prev];
      const lastIndex = next.length - 1;
      if (lastIndex >= 0 && next[lastIndex].role === 'assistant') {
        next[lastIndex] = {
          ...next[lastIndex],
          sources: Array.isArray(sources) ? sources : []
        };
      }
      return next;
    });
  };

  // ✅ Parser SSE robusto: separa eventos por \n\n OU \r\n\r\n
  const parseSseChunk = (raw) => {
    // retorna { events: string[], rest: string }
    const parts = raw.split(/\r?\n\r?\n/);
    const rest = parts.pop() || '';
    return { events: parts, rest };
  };

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!input.trim() || isLoading) {
      console.log('❌ Validação falhou');
      return;
    }

    console.log('🚀 Enviando mensagem...');
    console.log('🧠 Reasoning Mode:', reasoningMode);
    console.log('🌐 Web Search:', useWebSearch);

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    const assistantPlaceholder = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      sources: []
    };

    const messageHistory = [...messages];

    setMessages(prev => [...prev, userMessage, assistantPlaceholder]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const reasoningParams = getReasoningParams(model, reasoningMode);

      // ✅ Usa fetch com retry automático de token (renova sessão em caso de 401/403)
      const response = await chatFetchWithAuth('/api/chatai/stream', {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          model,
          messages: [...messageHistory, userMessage],
          systemPrompt,
          contextContent,
          useTools,
          casoId,
          codCli,
          codProcesso,
          threadId: threadIdRef.current,
          groundSearch: useWebSearch,
          groundSearchMode: 'required',
          verbosity: useVerbosity ? 'high' : undefined,
          ...reasoningParams
        }),
      }, refreshAccessToken, 1);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const serverThreadId = response.headers.get('X-Thread-Id');
      if (serverThreadId) {
        const tid = parseInt(serverThreadId);
        if (!isNaN(tid)) {
          console.log(`🆕 Thread ID: ${tid}`);
          threadIdRef.current = tid;
          setInternalThreadId(tid);
        }
      }

      if (!response.body) throw new Error('Stream não suportado');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      let textEventCount = 0;

      const processEventBlock = (evt) => {
        // evt pode ter múltiplas linhas data:
        const lines = evt.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

        const dataLines = lines
          .filter(l => l.startsWith('data:'))
          .map(l => l.substring(5).trim());

        if (!dataLines.length) return;

        const dataStr = dataLines.join('\n');

        if (dataStr === '[DONE]') {
          console.log('🏁 Stream [DONE]');
          return;
        }

        try {
          const parsed = JSON.parse(dataStr);

          if (parsed.error) throw new Error(parsed.error);

          const delta = extractTextDelta(parsed);
          if (delta) {
            textEventCount++;
            appendAssistantText(delta);
          }

          if (parsed.sources) {
            console.log('🔗 SOURCES RECEBIDOS via SSE:', parsed.sources);
            setAssistantSources(parsed.sources);
          }
        } catch (err) {
          console.warn('⚠️ Parse SSE error:', err.message);
          console.warn('⚠️ SSE data (até 200 chars):', dataStr.substring(0, 200));
        }
      };

      console.log('📖 Lendo stream SSE (parser robusto)...');

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // ✅ CRÍTICO: processar o RESTO do buffer ao final
          if (buffer.trim()) {
            const { events } = parseSseChunk(buffer + '\n\n'); // força fechamento
            for (const evt of events) processEventBlock(evt);
          }
          console.log('✅ Stream concluído');
          console.log('🧾 Text events recebidos:', textEventCount);
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const { events, rest } = parseSseChunk(buffer);
        buffer = rest;

        for (const evt of events) {
          processEventBlock(evt);
        }
      }

    } catch (err) {
      console.error('❌ Stream error:', err);
      setError(err);

      setMessages(prev => {
        const next = [...prev];
        const lastIndex = next.length - 1;
        if (lastIndex >= 0 && next[lastIndex].role === 'assistant') {
          next[lastIndex] = {
            ...next[lastIndex],
            content: `❌ Erro: ${err.message}`,
            error: true
          };
        }
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    input,
    isLoading,
    messages,
    model,
    systemPrompt,
    contextContent,
    useTools,
    casoId,
    codCli,
    codProcesso,
    reasoningMode,
    useWebSearch,
    useVerbosity,
    refreshAccessToken,
    getReasoningParams
  ]);

  const getProcessedMessages = useCallback(() => {
    return messages.map((msg, index) => ({
      id: msg.id || `msg-${index}`,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp || new Date().toISOString(),
      error: msg.error || false,
      sources: msg.sources || []
    }));
  }, [messages]);

  return {
    messages: getProcessedMessages(),
    input,
    isLoading,
    error,
    handleInputChange,
    handleSubmit,
    setInput,
    setMessages,
    threadId: internalThreadId,
    setThreadId: (newThreadId) => {
      threadIdRef.current = newThreadId;
      setInternalThreadId(newThreadId);
    },
    hasActiveTools: false,         // ✅ JÁ EXISTE (manter)
    activeToolCalls: new Map(),    // ✅ JÁ EXISTE (manter)
    toolResults: new Map(),        // ✅ JÁ EXISTE (manter)
    reload: () => {},
    stop: () => {},
    append: () => {}
  };

}
